import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./QuickDateFilter.module.css";

/* ---------- date helpers ---------- */
const fmt = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())
    .toISOString()
    .slice(0, 10);
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);
const startOfWeek = (d) => {
  const day = d.getDay();
  const diff = (day + 6) % 7; // Mon=0
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
};
const endOfWeek = (d) => addDays(startOfWeek(d), 6);

export function computeRangeISO(key) {
  const today = new Date();
  const map = {
    this_week: () => ({ from: fmt(startOfWeek(today)), to: fmt(endOfWeek(today)) }),
    this_month: () => ({ from: fmt(startOfMonth(today)), to: fmt(endOfMonth(today)) }),
    this_year: () => ({ from: fmt(startOfYear(today)), to: fmt(endOfYear(today)) }),
    last_week: () => {
      const end = addDays(startOfWeek(today), -1);
      const start = addDays(end, -6);
      return { from: fmt(start), to: fmt(end) };
    },
    last_month: () => {
      const prev = new Date(today.getFullYear(), today.getMonth() - 1, 15);
      return { from: fmt(startOfMonth(prev)), to: fmt(endOfMonth(prev)) };
    },
    last_year: () => {
      const y = today.getFullYear() - 1;
      return { from: fmt(new Date(y, 0, 1)), to: fmt(new Date(y, 11, 31)) };
    },
    last_7_days: () => ({ from: fmt(addDays(today, -6)), to: fmt(today) }),
    last_30_days: () => ({ from: fmt(addDays(today, -29)), to: fmt(today) }),
    last_60_days: () => ({ from: fmt(addDays(today, -59)), to: fmt(today) }),
    last_90_days: () => ({ from: fmt(addDays(today, -89)), to: fmt(today) }),
    last_180_days: () => ({ from: fmt(addDays(today, -179)), to: fmt(today) }),
    last_365_days: () => ({ from: fmt(addDays(today, -364)), to: fmt(today) }),
  };
  return map[key]?.() || null;
}

/* ---------- labels & layout ---------- */
const LABELS = {
  this_week: "This week",
  this_month: "This month",
  this_year: "This year",
  last_week: "Last week",
  last_month: "Last month",
  last_year: "Last year",
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  last_60_days: "Last 60 days",
  last_90_days: "Last 90 days",
  last_180_days: "Last 180 days",
  last_365_days: "Last 365 days",
};

const PRESET_ROWS = [
  ["this_week", "this_month", "this_year"],
  ["last_week", "last_month", "last_year"],
  ["last_7_days", "last_30_days", "last_60_days"],
  ["last_90_days", "last_180_days", "last_365_days"],
];

export default function QuickDateFilter({
  /** One of the keys above; optional when using custom range */
  currentRangeKey = "",
  /** Custom range (ISO yyyy-mm-dd); either can be empty */
  currentFrom = "",
  currentTo = "",
  /** onPick({ type:'range', key }) or onPick({ type:'custom', from, to }) */
  onPick,
  onClear,
}) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, anchorX: 24 });

  // custom form state
  const [from, setFrom] = useState(currentFrom || "");
  const [to, setTo] = useState(currentTo || "");

  const activeLabel = useMemo(() => {
    if (currentRangeKey && LABELS[currentRangeKey]) return LABELS[currentRangeKey];
    if (currentFrom || currentTo) {
      const lo = currentFrom || "…";
      const hi = currentTo || "…";
      return `${lo} – ${hi}`;
    }
    return "Date";
  }, [currentRangeKey, currentFrom, currentTo]);

  const recalcPos = () => {
    const el = btnRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();

    // How far from the screen edges the popover is allowed to get
    const SIDE_PAD =
      16 + // visual breathing room
        (typeof window !== "undefined" && window.CSS?.supports?.("padding: env(safe-area-inset-right)"))
        ? parseFloat(getComputedStyle(document.documentElement)
          .getPropertyValue("--sat") || "0") // (optional) if you already expose one
        : 0;

    const gap = 8;
    const maxWidth = 360;
    const width = Math.min(maxWidth, Math.max(260, r.width));

    // Try to center the popover under the button, then clamp to viewport with side padding
    const centered = r.left + r.width / 2 - width / 2;
    const minLeft = SIDE_PAD;
    const maxLeft = window.innerWidth - width - SIDE_PAD;
    const left = Math.min(Math.max(minLeft, centered), maxLeft);

    // Caret X relative to the popover box, clamped inside by ~18px
    const anchorX = Math.min(width - 18, Math.max(18, r.left - left + r.width / 2));

    setPos({ top: r.bottom + gap, left, width, anchorX });
  };


  useEffect(() => {
    if (!open) return;
    // hydrate custom inputs from current values each time menu opens
    setFrom(currentFrom || "");
    setTo(currentTo || "");
    recalcPos();
    const onWin = () => recalcPos();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commitPreset = (key) => {
    const r = computeRangeISO(key);
    if (!r) return;
    onPick?.({ type: "range", key });
    setOpen(false);
  };

  const applyCustom = () => {
    const cleanFrom = (from || "").trim();
    const cleanTo = (to || "").trim();
    onPick?.({ type: "custom", from: cleanFrom, to: cleanTo });
    setOpen(false);
  };

  const commitClear = () => {
    onClear?.();
    setOpen(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`btn btn-outline-secondary btn-sm btn-compact ${styles.trigger}`}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((v) => !v)}
        title="Quick date filter"
      >
        {activeLabel} <span aria-hidden="true">▾</span>
      </button>

      {open &&
        createPortal(
          <>
            <div className={styles.backdrop} onMouseDown={() => setOpen(false)} />
            <div
              className={styles.menu}
              style={{
                top: `${pos.top}px`,
                left: `${pos.left}px`,
                width: `${pos.width}px`,
                "--qd-anchor-x": `${pos.anchorX}px`,
              }}
              role="dialog"
              aria-label="Quick date filter"
            >
              {/* Presets */}
              {PRESET_ROWS.map((row, i) => (
                <div key={i} className={styles.row}>
                  {row.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => commitPreset(key)}
                      className={`${styles.pill} ${currentRangeKey === key ? styles.active : ""}`}
                    >
                      {LABELS[key]}
                    </button>
                  ))}
                </div>
              ))}

              {/* Custom range */}
              <div className={styles.custom}>
                <div className={styles.customLabel}>Custom range</div>

                <div className={styles.customGrid}>
                  <div className={styles.field}>
                    <label htmlFor="qd-from" className={styles.fieldLabel}>From</label>
                    <input
                      id="qd-from"
                      type="date"
                      className="form-control form-control-sm"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="qd-to" className={styles.fieldLabel}>To</label>
                    <input
                      id="qd-to"
                      type="date"
                      className="form-control form-control-sm"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.customActions}>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={commitClear}>Clear</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={applyCustom}>Apply</button>
                </div>
              </div>

            </div>
          </>,
          document.body
        )}
    </>
  );
}
